import { create } from 'zustand'
import type { Card } from '../types'

export interface AudioQueueItem {
  cardId: number
  provider: 'file' | 'soundcloud'
  audioUrl: string
  title: string
  subtext: string
  coverUrl?: string
}

interface AudioPlayerState {
  queue: AudioQueueItem[]
  activeIndex: number
  wantedPlaying: boolean
  isPlaying: boolean
  currentTime: number
  duration: number
  setQueueFromCards: (cards: Card[]) => void
  toggleTrackByCardId: (cardId: number) => void
  playNext: () => void
  playPrev: () => void
  setPlaybackState: (isPlaying: boolean) => void
  setCurrentTime: (time: number) => void
  setDuration: (duration: number) => void
  requestPlay: () => void
  requestPause: () => void
  clearActive: () => void
}

function buildQueue(cards: Card[]): AudioQueueItem[] {
  const demoAudioUrl = 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3'
  const isDev = import.meta.env.DEV
  const isSoundCloudUrl = (raw: string) => /(^https?:\/\/)?((www|m)\.)?(soundcloud\.com|on\.soundcloud\.com)\//i.test(raw.trim())

  return cards
    .filter((card) => card.card_type === 'audio')
    .sort((a, b) => {
      if (a.grid_y !== b.grid_y) return a.grid_y - b.grid_y
      if (a.grid_x !== b.grid_x) return a.grid_x - b.grid_x
      return a.id - b.id
    })
    .map((card) => {
      const content = card.content as any
      const rawAudioUrl = (content.audioUrl || '').trim()
      const soundcloudUrl = (content.audioSoundcloudUrl || '').trim()
      const hasSoundcloud = isSoundCloudUrl(soundcloudUrl)
      const audioUrl = (hasSoundcloud ? soundcloudUrl : '') || rawAudioUrl || (isDev ? demoAudioUrl : '')
      const provider: AudioQueueItem['provider'] = hasSoundcloud ? 'soundcloud' : 'file'
      return {
        cardId: card.id,
        provider,
        audioUrl,
        title: (content.audioTitle || content.name || '').trim(),
        subtext: (content.audioSubtext || content.subtext || '').trim(),
        coverUrl: (content.audioCoverUrl || content.cover || '').trim() || undefined,
      }
    })
    .filter((track) => track.audioUrl.length > 0)
}

export const useAudioPlayerStore = create<AudioPlayerState>((set, get) => ({
  queue: [],
  activeIndex: -1,
  wantedPlaying: false,
  isPlaying: false,
  currentTime: 0,
  duration: 0,

  setQueueFromCards: (cards) => {
    const nextQueue = buildQueue(cards)
    const { queue, activeIndex } = get()
    const activeCardId = activeIndex >= 0 ? queue[activeIndex]?.cardId : undefined
    const nextActiveIndex = activeCardId != null ? nextQueue.findIndex((q) => q.cardId === activeCardId) : -1

    set((state) => ({
      queue: nextQueue,
      activeIndex: nextActiveIndex,
      wantedPlaying: nextActiveIndex >= 0 ? state.wantedPlaying : false,
      isPlaying: nextActiveIndex >= 0 ? state.isPlaying : false,
      currentTime: nextActiveIndex >= 0 ? state.currentTime : 0,
      duration: nextActiveIndex >= 0 ? state.duration : 0,
    }))
  },

  toggleTrackByCardId: (cardId) => {
    const { queue, activeIndex, wantedPlaying } = get()
    const index = queue.findIndex((q) => q.cardId === cardId)
    if (index < 0) return
    if (index === activeIndex) {
      set({ wantedPlaying: !wantedPlaying })
      return
    }
    set({
      activeIndex: index,
      wantedPlaying: true,
      isPlaying: false,
      currentTime: 0,
      duration: 0,
    })
  },

  playNext: () => {
    const { queue, activeIndex } = get()
    if (queue.length === 0) return
    const nextIndex = activeIndex < 0 ? 0 : (activeIndex + 1) % queue.length
    set({
      activeIndex: nextIndex,
      wantedPlaying: true,
      isPlaying: false,
      currentTime: 0,
      duration: 0,
    })
  },

  playPrev: () => {
    const { queue, activeIndex } = get()
    if (queue.length === 0) return
    const prevIndex = activeIndex < 0 ? 0 : (activeIndex - 1 + queue.length) % queue.length
    set({
      activeIndex: prevIndex,
      wantedPlaying: true,
      isPlaying: false,
      currentTime: 0,
      duration: 0,
    })
  },

  setPlaybackState: (isPlaying) => set({ isPlaying }),
  setCurrentTime: (time) => set({ currentTime: Math.max(0, time || 0) }),
  setDuration: (duration) => set({ duration: Math.max(0, duration || 0) }),
  requestPlay: () => set({ wantedPlaying: true }),
  requestPause: () => set({ wantedPlaying: false }),
  clearActive: () =>
    set({
      activeIndex: -1,
      wantedPlaying: false,
      isPlaying: false,
      currentTime: 0,
      duration: 0,
    }),
}))
